import React, { useState, useRef } from 'react';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeManyPresetStickers, setPresetStickers, deletePresetStickers, PresetStickersResponse } from '@/lib/collections/presetStickers';
import { uploadAppFiles, getAppFiles } from '@/lib/collections/appFiles';
import { Time } from '@/lib/db-client';
import { generateRandomId } from '@/lib/utils';
import { toast } from 'sonner';
import { Plus, Trash2, X, Layers, ImageIcon } from 'lucide-react';

// Danger Room color constants (mirrored from DangerRoomPage)
const DANGER_RED = '#E63946';
const DANGER_ORANGE = '#FF6B35';
const ACCENT_GREEN = '#7CFC00';
const BG_DARK = '#080d1f';
const BG_CARD = 'rgba(14, 18, 38, 0.98)';
const BORDER = 'rgba(230, 57, 70, 0.2)';

interface StickerManagerProps {
  open: boolean;
  onClose: () => void;
  walletAddress: string;
}

export const StickerManager: React.FC<StickerManagerProps> = ({ open, onClose, walletAddress }) => {
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingPreview, setPendingPreview] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: stickers } = useRealtimeData<PresetStickersResponse[]>(
    subscribeManyPresetStickers,
    open,
  );

  const sortedStickers = [...(stickers ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFile(file);
    setPendingPreview(URL.createObjectURL(file));
    // Auto-fill name from filename (strip extension)
    const autoName = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ').trim();
    setNameInput(autoName);
    setShowAddForm(true);
  };

  const handleAdd = async () => {
    if (!pendingFile) {
      toast.error('Select an image first');
      return;
    }
    const name = nameInput.trim();
    if (!name) {
      toast.error('Enter a sticker name');
      return;
    }

    setUploading(true);
    try {
      const fileId = generateRandomId();
      const uploaded = await uploadAppFiles(fileId, pendingFile);
      if (!uploaded) {
        toast.error('Image upload failed');
        return;
      }

      const fileItem = await getAppFiles(fileId);
      if (!fileItem?.url) {
        toast.error('Could not get uploaded image URL');
        return;
      }

      const stickerId = generateRandomId();
      const maxOrder = sortedStickers.length > 0
        ? Math.max(...sortedStickers.map(s => s.order ?? 0))
        : -1;

      const success = await setPresetStickers(stickerId, {
        imageUrl: fileItem.url,
        name,
        order: maxOrder + 1,
        addedBy: walletAddress,
        addedAt: Time.Now,
      });

      if (success) {
        toast.success(`Sticker "${name}" added!`);
        resetForm();
      } else {
        toast.error('Failed to save sticker — permission denied');
      }
    } catch {
      toast.error('Something went wrong');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (sticker: PresetStickersResponse) => {
    setDeletingId(sticker.id);
    const success = await deletePresetStickers(sticker.id);
    if (!success) {
      toast.error('Failed to delete sticker');
    } else {
      toast.success(`Sticker "${sticker.name}" removed`);
    }
    setDeletingId(null);
  };

  const resetForm = () => {
    setPendingFile(null);
    setPendingPreview(null);
    setNameInput('');
    setShowAddForm(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(4px)',
      }}
      onClick={e => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        style={{
          background: BG_DARK,
          border: `1px solid ${BORDER}`,
          borderRadius: 10,
          width: '100%',
          maxWidth: 520,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: `0 0 60px rgba(230,57,70,0.2), 0 0 0 1px rgba(230,57,70,0.08)`,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '14px 18px',
            borderBottom: `1px solid ${BORDER}`,
            background: BG_CARD,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexShrink: 0,
          }}
        >
          {/* Hazard stripe accent */}
          <div
            style={{
              width: 3,
              height: 26,
              background: `repeating-linear-gradient(-45deg, ${DANGER_RED} 0px, ${DANGER_RED} 4px, ${DANGER_ORANGE} 4px, ${DANGER_ORANGE} 8px)`,
              borderRadius: 2,
              flexShrink: 0,
            }}
          />
          <Layers size={16} style={{ color: DANGER_RED }} />
          <span
            style={{
              fontFamily: "'Bangers', cursive",
              fontSize: '1.2rem',
              letterSpacing: '0.12em',
              color: DANGER_RED,
              flex: 1,
            }}
          >
            MANAGE STICKERS
          </span>
          <span
            style={{
              fontFamily: 'monospace',
              fontSize: '0.6rem',
              color: 'rgba(255,255,255,0.2)',
              marginRight: 8,
            }}
          >
            {sortedStickers.length} sticker{sortedStickers.length !== 1 ? 's' : ''}
          </span>
          <button
            onClick={handleClose}
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: `1px solid rgba(255,255,255,0.1)`,
              borderRadius: 5,
              color: 'rgba(255,255,255,0.4)',
              cursor: 'pointer',
              padding: '4px 5px',
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(230,57,70,0.15)';
              (e.currentTarget as HTMLButtonElement).style.color = DANGER_RED;
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.06)';
              (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.4)';
            }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Add form */}
        {showAddForm && (
          <div
            style={{
              padding: '14px 18px',
              borderBottom: `1px solid rgba(124,252,0,0.15)`,
              background: 'rgba(124,252,0,0.04)',
              flexShrink: 0,
            }}
          >
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              {/* Preview */}
              {pendingPreview && (
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: 6,
                    border: `1px solid rgba(124,252,0,0.3)`,
                    overflow: 'hidden',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(0,0,0,0.3)',
                  }}
                >
                  <img
                    src={pendingPreview}
                    alt="Preview"
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                </div>
              )}
              {/* Name input + actions */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input
                  value={nameInput}
                  onChange={e => setNameInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') resetForm(); }}
                  placeholder="Sticker name..."
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: `1px solid rgba(124,252,0,0.25)`,
                    borderRadius: 5,
                    color: 'rgba(255,255,255,0.9)',
                    fontSize: '0.85rem',
                    fontFamily: "'Barlow', sans-serif",
                    padding: '7px 11px',
                    outline: 'none',
                    width: '100%',
                  }}
                  onFocus={e => { (e.currentTarget as HTMLInputElement).style.borderColor = ACCENT_GREEN; }}
                  onBlur={e => { (e.currentTarget as HTMLInputElement).style.borderColor = 'rgba(124,252,0,0.25)'; }}
                  autoFocus
                />
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    onClick={handleAdd}
                    disabled={uploading || !nameInput.trim()}
                    style={{
                      flex: 1,
                      background: uploading || !nameInput.trim()
                        ? 'rgba(124,252,0,0.2)'
                        : `linear-gradient(135deg, ${ACCENT_GREEN} 0%, #4ade80 100%)`,
                      border: 'none',
                      borderRadius: 5,
                      color: uploading || !nameInput.trim() ? 'rgba(0,0,0,0.4)' : '#0a0a14',
                      cursor: uploading || !nameInput.trim() ? 'not-allowed' : 'pointer',
                      padding: '7px 14px',
                      fontFamily: "'Bangers', cursive",
                      fontSize: '0.85rem',
                      letterSpacing: '0.1em',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {uploading ? 'UPLOADING...' : 'ADD STICKER'}
                  </button>
                  <button
                    onClick={resetForm}
                    disabled={uploading}
                    style={{
                      background: 'rgba(255,255,255,0.06)',
                      border: `1px solid rgba(255,255,255,0.1)`,
                      borderRadius: 5,
                      color: 'rgba(255,255,255,0.4)',
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      padding: '7px 10px',
                      display: 'flex',
                      alignItems: 'center',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sticker grid */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 16,
          }}
        >
          {sortedStickers.length === 0 && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                padding: '48px 0',
                color: 'rgba(255,255,255,0.2)',
              }}
            >
              <Layers size={40} style={{ opacity: 0.25 }} />
              <p style={{ fontFamily: "'Bangers', cursive", fontSize: '1rem', letterSpacing: '0.12em' }}>
                NO STICKERS YET
              </p>
              <p style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'rgba(255,255,255,0.15)' }}>
                Add the first one below
              </p>
            </div>
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 10,
            }}
          >
            {sortedStickers.map(sticker => (
              <StickerManagerTile
                key={sticker.id}
                sticker={sticker}
                onDelete={handleDelete}
                isDeleting={deletingId === sticker.id}
              />
            ))}
          </div>
        </div>

        {/* Footer: Add button */}
        <div
          style={{
            padding: '12px 18px',
            borderTop: `1px solid ${BORDER}`,
            background: BG_CARD,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              background: 'rgba(230,57,70,0.1)',
              border: `1px dashed rgba(230,57,70,0.4)`,
              borderRadius: 6,
              color: DANGER_RED,
              cursor: uploading ? 'not-allowed' : 'pointer',
              padding: '9px 14px',
              fontFamily: "'Bangers', cursive",
              fontSize: '0.9rem',
              letterSpacing: '0.1em',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={e => {
              if (!uploading) {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(230,57,70,0.18)';
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(230,57,70,0.7)';
              }
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(230,57,70,0.1)';
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(230,57,70,0.4)';
            }}
          >
            <ImageIcon size={15} />
            ADD STICKER
          </button>
        </div>
      </div>
    </div>
  );
};

const StickerManagerTile: React.FC<{
  sticker: PresetStickersResponse;
  onDelete: (s: PresetStickersResponse) => void;
  isDeleting: boolean;
}> = ({ sticker, onDelete, isDeleting }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        aspectRatio: '1',
        borderRadius: 6,
        background: 'rgba(255,255,255,0.04)',
        border: hovered ? `1px solid rgba(230,57,70,0.4)` : `1px solid rgba(255,255,255,0.08)`,
        overflow: 'hidden',
        transition: 'border-color 0.15s ease',
      }}
    >
      <img
        src={sticker.imageUrl}
        alt={sticker.name}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
          padding: 6,
        }}
        loading="lazy"
      />

      {/* Name label */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'rgba(0,0,0,0.75)',
          padding: '3px 5px',
          transform: hovered ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.15s ease',
        }}
      >
        <p
          style={{
            fontFamily: 'monospace',
            fontSize: '0.52rem',
            color: 'rgba(255,255,255,0.6)',
            letterSpacing: '0.04em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            margin: 0,
          }}
        >
          {sticker.name}
        </p>
      </div>

      {/* Delete button */}
      {hovered && (
        <button
          onClick={() => onDelete(sticker)}
          disabled={isDeleting}
          title="Remove sticker"
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            background: 'rgba(230,57,70,0.85)',
            border: 'none',
            borderRadius: 4,
            color: '#fff',
            cursor: isDeleting ? 'not-allowed' : 'pointer',
            width: 20,
            height: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: isDeleting ? 0.5 : 1,
            transition: 'background 0.12s ease',
          }}
          onMouseEnter={e => {
            if (!isDeleting) (e.currentTarget as HTMLButtonElement).style.background = '#E63946';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLButtonElement).style.background = 'rgba(230,57,70,0.85)';
          }}
        >
          <Trash2 size={11} />
        </button>
      )}

      {isDeleting && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              border: `2px solid ${DANGER_RED}`,
              borderTopColor: 'transparent',
              animation: 'spin 0.6s linear infinite',
            }}
          />
        </div>
      )}
    </div>
  );
};

export default StickerManager;
